/**
 * CheckInSummaryConfirm
 *
 * Final step of a check-in: review and edit the proposed summary, confirm how
 * the focus area is trending, and save.
 *
 * The progress options are the one genuinely evaluative choice in the flow, so
 * they carry direction icons and their own colour rather than reading as three
 * identical grey buttons. Selection is expressed with aria-pressed, so the
 * state is not colour-only.
 */

import { useState } from "react";
import { Loader2, TrendingDown, Minus, TrendingUp, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import type { ProgressSignal } from "@/types/focusAreaCheckIn";

interface CheckInSummaryConfirmProps {
  proposedSummary: string;
  proposedProgressSignal: ProgressSignal;
  onSave: (data: { summary: string; progress_signal: ProgressSignal }) => void;
  isSaving: boolean;
}

const PROGRESS_OPTIONS: {
  value: ProgressSignal;
  label: string;
  icon: typeof TrendingUp;
  /** Applied only when selected, so unselected options stay quiet. */
  selectedClass: string;
}[] = [
  {
    value: "worse",
    label: "Harder",
    icon: TrendingDown,
    selectedClass:
      "border-amber-500 bg-amber-50 text-amber-900 dark:bg-amber-950 dark:text-amber-100",
  },
  {
    value: "same",
    label: "About the same",
    icon: Minus,
    selectedClass: "border-primary bg-muted text-foreground",
  },
  {
    value: "better",
    label: "Better",
    icon: TrendingUp,
    selectedClass:
      "border-green-600 bg-green-50 text-green-900 dark:bg-green-950 dark:text-green-100",
  },
];

export function CheckInSummaryConfirm({
  proposedSummary,
  proposedProgressSignal,
  onSave,
  isSaving,
}: CheckInSummaryConfirmProps) {
  const [summary, setSummary] = useState(proposedSummary);
  const [progressSignal, setProgressSignal] = useState<ProgressSignal>(
    proposedProgressSignal
  );

  const handleSave = () => {
    onSave({ summary, progress_signal: progressSignal });
  };

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-6 p-4 sm:p-6">
      <div className="flex items-start gap-3">
        <span
          className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300"
          aria-hidden="true"
        >
          <Check className="h-5 w-5" />
        </span>
        <div>
          <h2 className="text-xl font-semibold leading-tight text-foreground">
            Review your check-in
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            This is what we captured. Edit anything that doesn't sound right —
            it's your record.
          </p>
        </div>
      </div>

      {/* Summary */}
      <div className="flex flex-col gap-2">
        <Label htmlFor="check-in-summary">Summary</Label>
        <Textarea
          id="check-in-summary"
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          className="min-h-[140px] leading-relaxed"
          data-testid="summary-textarea"
        />
        {summary.trim().length === 0 && (
          <p className="text-xs text-destructive">
            A summary is needed before saving.
          </p>
        )}
      </div>

      {/* Progress signal */}
      <div className="flex flex-col gap-2">
        <Label>How is this area going?</Label>
        <div
          className="grid grid-cols-3 gap-2"
          role="group"
          aria-label="Progress signal"
        >
          {PROGRESS_OPTIONS.map((option) => {
            const Icon = option.icon;
            const isSelected = progressSignal === option.value;

            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setProgressSignal(option.value)}
                data-testid={`progress-${option.value}`}
                aria-pressed={isSelected}
                className={[
                  "flex flex-col items-center gap-1.5 rounded-lg border px-2 py-3 text-xs font-medium transition-colors",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  isSelected
                    ? option.selectedClass
                    : "border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground",
                ].join(" ")}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                <span className="text-center leading-tight">
                  {option.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <Button
        onClick={handleSave}
        disabled={isSaving || summary.trim().length === 0}
        className="w-full"
        size="lg"
        data-testid="save-check-in-button"
      >
        {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        {isSaving ? "Saving..." : "Save check-in"}
      </Button>
    </div>
  );
}
